/* VEKT Theme — JavaScript
   Cart drawer, mobile menu, sticky header, product variants, toast
   ----------------------------------------------------------------- */

'use strict';

/* ── Utilities ─────────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function formatMoney(cents) {
  const cfg = window.VEKT || {};
  const currency = cfg.currency || 'EUR';
  const locale = cfg.locale || 'pt-PT';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch (e) {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }
}

/* Set a button's visible label without destroying an inner <span> wrapper */
function setBtnLabel(btn, text) {
  if (!btn) return;
  const span = btn.querySelector('span');
  if (span) span.textContent = text;
  else btn.textContent = text;
}

/* ── Toast ─────────────────────────────────────────────────────── */
let toastTimer;
function showToast(msg, duration = 2400) {
  const el = $('#vk-toast');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('vk-toast--show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('vk-toast--show'), duration);
}

/* ── Cart ──────────────────────────────────────────────────────── */
const Cart = {
  drawer:   $('#vk-cart-drawer'),
  overlay:  $('#vk-overlay'),
  body:     $('#vk-cart-body'),
  countEl:  $('#vk-cart-count'),
  subtotal: $('#vk-cart-subtotal'),

  async fetch() {
    const r = await fetch('/cart.js');
    return r.json();
  },

  async add(variantId, qty = 1) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    });
    if (!r.ok) {
      const err = await r.json();
      throw new Error(err.description || 'ERROR ADDING TO CART');
    }
    return r.json();
  },

  async change(key, qty) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    });
    return r.json();
  },

  async open() {
    await this.refresh();
    this.drawer?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.classList.add('vk-cart-open');
    this.drawer?.setAttribute('aria-hidden', 'false');
  },

  close() {
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.classList.remove('vk-cart-open');
    this.drawer?.setAttribute('aria-hidden', 'true');
  },

  async refresh() {
    const cart = await this.fetch();
    this.render(cart);
  },

  render(cart) {
    if (this.countEl) this.countEl.textContent = cart.item_count;
    if (this.subtotal) this.subtotal.textContent = formatMoney(cart.total_price);

    // Update header cart count badge
    $$('.vk-header-cart-count').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });

    if (!this.body) return;

    if (cart.items.length === 0) {
      this.body.innerHTML = `
        <div class="vk-cart-empty">
          <div class="vk-cart-empty__title">YOUR CART IS EMPTY</div>
          <div class="vk-cart-empty__sub">ADD A VK-001 TO BEGIN</div>
        </div>`;
      return;
    }

    const free = 4000; // €40 in cents
    const progress = Math.min(100, (cart.total_price / free) * 100);
    const remaining = Math.max(0, free - cart.total_price);

    let html = `
      <div class="vk-cart-progress">
        <div class="vk-cart-progress__meta">
          <span>${remaining > 0 ? 'FREE SHIPPING OVER €40' : 'FREE SHIPPING INCLUDED ✓'}</span>
          ${remaining > 0 ? `<strong>AWAY ${formatMoney(remaining)}</strong>` : ''}
        </div>
        <div class="vk-cart-progress__bar"><span style="width:${progress}%"></span></div>
      </div>
      <div class="vk-cart-items">`;

    for (const item of cart.items) {
      html += `
        <div class="vk-cart-item" data-key="${item.key}">
          <div class="vk-cart-item__img">
            ${item.image
              ? `<img src="${item.image}" alt="${item.title}" width="80" loading="lazy">`
              : '<div class="vk-cart-item__placeholder"></div>'}
          </div>
          <div class="vk-cart-item__info">
            <div class="vk-cart-item__name">${item.product_title}</div>
            ${item.variant_title && item.variant_title !== 'Default Title'
              ? `<div class="vk-cart-item__variant">${item.variant_title}</div>` : ''}
            <div class="vk-cart-item__qty-row">
              <button class="vk-qty-btn" data-key="${item.key}" data-delta="-1">−</button>
              <span class="vk-qty-val">${item.quantity}</span>
              <button class="vk-qty-btn" data-key="${item.key}" data-delta="1">+</button>
            </div>
          </div>
          <div class="vk-cart-item__right">
            <div class="vk-cart-item__price">${formatMoney(item.final_line_price)}</div>
            <button class="vk-cart-item__remove" data-key="${item.key}">Remove</button>
          </div>
        </div>`;
    }
    html += '</div>';
    this.body.innerHTML = html;

    // Bind qty buttons
    $$('.vk-qty-btn', this.body).forEach(btn => {
      btn.addEventListener('click', async () => {
        const cart = await this.fetch();
        const item = cart.items.find(i => i.key === btn.dataset.key);
        if (!item) return;
        const newQty = Math.max(0, item.quantity + parseInt(btn.dataset.delta));
        await this.change(btn.dataset.key, newQty);
        this.refresh();
      });
    });

    // Bind remove buttons
    $$('.vk-cart-item__remove', this.body).forEach(btn => {
      btn.addEventListener('click', async () => {
        await this.change(btn.dataset.key, 0);
        this.refresh();
      });
    });
  },

  init() {
    this.drawer  = $('#vk-cart-drawer');
    this.overlay = $('#vk-overlay');
    this.body    = $('#vk-cart-body');
    this.countEl = $('#vk-cart-count');
    this.subtotal = $('#vk-cart-subtotal');

    $('#vk-cart-close')?.addEventListener('click', () => this.close());
    $('#vk-overlay')?.addEventListener('click', () => this.close());

    // Open cart on cart icon click
    $$('[data-cart-trigger]').forEach(el => {
      el.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });

    // Intercept add-to-cart forms (single source of truth for AJAX add)
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput || !variantInput.value) return;
      const qty = parseInt(form.querySelector('[name="quantity"]')?.value || 1);
      const btn = form.querySelector('[type="submit"]');
      if (btn && btn.dataset.vkBusy === '1') return; // guard re-entry
      const defaultText = btn?.dataset.defaultText || 'Add to cart';
      if (btn) { btn.dataset.vkBusy = '1'; btn.disabled = true; setBtnLabel(btn, '...'); }
      try {
        await this.add(variantInput.value, qty);
        if (btn) { setBtnLabel(btn, 'Added \u2713'); btn.classList.add('added'); }
        this.open();
      } catch (err) {
        showToast(err.message);
        if (btn) setBtnLabel(btn, defaultText);
      } finally {
        if (btn) {
          setTimeout(() => {
            setBtnLabel(btn, defaultText);
            btn.classList.remove('added');
            btn.disabled = false;
            delete btn.dataset.vkBusy;
          }, 2200);
        }
      }
    });

    // Allow other scripts (e.g. PDP) to open the drawer after their own add
    document.addEventListener('vk:cart:open', () => this.open());

    // Initial count
    this.fetch().then(cart => {
      if (this.countEl) this.countEl.textContent = cart.item_count;
      $$('.vk-header-cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
    });
  },
};

/* ── Mobile Menu ───────────────────────────────────────────────── */
const MobileMenu = {
  menu: null,
  overlay: null,

  open() {
    this.menu?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.classList.add('vk-menu-open');
    this.menu?.setAttribute('aria-hidden', 'false');
  },
  close() {
    this.menu?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.classList.remove('vk-menu-open');
    this.menu?.setAttribute('aria-hidden', 'true');
  },
  init() {
    this.menu = $('#vk-mobile-menu');
    this.overlay = $('#vk-overlay');
    $('#vk-menu-toggle')?.addEventListener('click', () => this.open());
    $('#vk-menu-close')?.addEventListener('click', () => this.close());
  },
};

/* ── Sticky Header ─────────────────────────────────────────────── */
function initStickyHeader() {
  const header = $('.vk-header');
  if (!header) return;
  let lastY = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    if (y > 80) {
      header.classList.add('vk-header--scrolled');
    } else {
      header.classList.remove('vk-header--scrolled');
    }
    lastY = y;
  }, { passive: true });
}

/* ── Product Variant Selector ──────────────────────────────────── */
function initVariantSelector() {
  $$('[data-variant-picker]').forEach(picker => {
    const form = picker.closest('[data-product-form]');
    const idInput = form?.querySelector('[name="id"]');
    const priceEl = form?.querySelector('[data-price]');
    const submitBtn = form?.querySelector('[type="submit"]');

    picker.addEventListener('change', (e) => {
      const selected = [...picker.querySelectorAll('input[type="radio"]')]
        .filter(r => r.checked)
        .map(r => r.value);

      // Find the matching variant from the product JSON (scoped to this section)
      const scope = picker.closest('section') || document;
      const jsonEl = scope.querySelector('[data-product-json]') || $('[data-product-json]');
      const productData = JSON.parse(jsonEl?.textContent || 'null');
      if (!productData) return;

      const variant = productData.variants.find(v =>
        v.options.every((opt, i) => opt === selected[i])
      );

      if (variant && idInput) {
        idInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (submitBtn) {
          submitBtn.disabled = !variant.available;
          setBtnLabel(submitBtn, variant.available
            ? (submitBtn.dataset.defaultText || 'Add to cart')
            : 'SOLD OUT');
        }
        // Swap the hero image to this variant's image, if it has one
        const fpImg = scope.querySelector('[data-fp-image]');
        const vImg = variant.featured_image && variant.featured_image.src;
        if (fpImg && vImg) {
          const next = vImg.startsWith('http') ? vImg : 'https:' + vImg;
          if (fpImg.getAttribute('src') !== next) {
            fpImg.style.opacity = '0';
            const swap = () => {
              fpImg.src = next;
              if (variant.featured_image.alt) fpImg.alt = variant.featured_image.alt;
              fpImg.style.opacity = '1';
            };
            const pre = new Image();
            pre.onload = swap;
            pre.onerror = swap;
            pre.src = next;
          }
        }
        // Update URL without reload
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    });
  });
}

/* ── Bootstrap ─────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  Cart.init();
  MobileMenu.init();
  initStickyHeader();
  initVariantSelector();
});

/* Expose for inline page scripts */
window.VektCart = Cart;
